/**
 * 流式对话工具函数
 * 使用 Fetch API + ReadableStream 实现流式响应
 */

/**
 * 流式发送消息并接收响应
 * @param {Object|string} data - 请求体数据（对象或字符串），直接作为请求体发送，不包装 message
 * @param {string|number} sessionId - 会话 ID
 * @param {Object} options - 配置选项
 * @param {string} options.apiUrl - API 地址，默认 `/api/careerPlan/session/${sessionId}/chat`
 * @param {Function} options.onChunk - 接收到数据块时的回调函数 (chunk, fullText) => {}
 * @param {Function} options.onComplete - 流完成时的回调函数 (fullText, doneData) => {}
 * @param {Function} options.onError - 错误回调函数 (error) => {}
 * @param {Object} options.headers - 自定义请求头
 * @returns {Promise<string>} 返回完整的响应文本
 */

import { getToken } from '@/store/local/auth'

export async function streamChat(data, sessionId, options = {}) {
  let isDone = false
  const {
    apiUrl = `/api/careerPlan/session/${sessionId}/chat`,
    onChunk = null,
    onComplete = null,
    onError = null,
    headers = {}
  } = options

  let fullText = ''

  try {
    // 获取 token
    const token = getToken()
    
    // 构建请求头
    const requestHeaders = {
      'Content-Type': 'application/json', // 请求体是 JSON 格式
      'Accept': 'text/event-stream', // 期望接收流式响应（SSE 格式）
      'Cache-Control': 'no-cache', // 禁用缓存，确保实时流式响应
      'terminal': '4', // 统一追加终端标识
      ...headers
    }
    
    // 如果有 token，添加到请求头
    if (token) {
      requestHeaders['token'] = token
    }
    
    // 构建请求体：直接使用传递的数据，不包装 message
    // 如果 data 是字符串，转换为对象；否则直接使用
    const requestBody = typeof data === 'string' ? { content: data } : data
    
    // 发送请求
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: requestHeaders,
      credentials: 'include', // 跨域请求时发送 cookie
      body: JSON.stringify(requestBody)
    })

    if (!response.ok) {
      // 如果是 500 状态码，尝试解析响应体获取错误信息
      if (response.status === 500) {
        try {
          const errorData = await response.json()
          const error = new Error(errorData.msg || '咨询的人太多了，成为会员开启定制化职业规划之旅')
          error.status = 500
          error.responseData = errorData
          throw error
        } catch (parseError) {
          // 如果解析失败，使用默认错误信息
          const error = new Error('咨询的人太多了，成为会员开启定制化职业规划之旅')
          error.status = 500
          throw error
        }
      }
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    // 读取流
    const reader = response.body.getReader()
    const decoder = new TextDecoder('utf-8')
    let buffer = ''

    while (!isDone) {
      const { done, value } = await reader.read()
      
      if (done) {
        // 处理最后剩余的 buffer
        if (buffer && typeof buffer === 'string' && buffer.trim()) {
          const trimmedLine = buffer.trim()
          if (trimmedLine && trimmedLine.startsWith('data:')) {
            try {
              // 确保 trimmedLine 长度足够
              if (trimmedLine.length < 5) {
                isDone = true
                break
              }
              const jsonStr = trimmedLine && trimmedLine.slice(5).trim()
              if (!jsonStr) {
                isDone = true
                break
              }
              const data = JSON.parse(jsonStr)
              
              // 检查是否是 error 类型，如果是则直接终止
              if (data.type === 'error') {
                isDone = true
                const errorMessage = data.error || '模型服务错误'
                const error = new Error(errorMessage)
                if (onError) {
                  onError(error)
                }
                throw error
              }
              
              // 检查是否是 done 类型
              if (data.type === 'done' || data.done === true) {
                isDone = true
                // 如果后端返回了 full_text，优先使用它（更完整准确）
                const finalText = data.full_text || data.fullText || fullText
                if (onComplete) {
                  onComplete(finalText, data)
                }
                return finalText
              }
              
              // 处理 delta
              if (data.delta) {
                fullText += data.delta
                if (onChunk) {
                  onChunk(data.delta, fullText)
                }
              }
            } catch (e) {
              // 忽略解析错误
              console.warn('Failed to parse final buffer:', trimmedLine, e)
            }
          }
        }
        isDone = true
        // 流结束
        break
      }

      // 解码数据块（实时处理，不等待完整行）
      if (value) {
        const chunk = decoder.decode(value, { stream: true })
        buffer += chunk
        
        // 实时处理 buffer 中的完整行
        let newlineIndex
        while ((newlineIndex = buffer.indexOf('\n')) !== -1) {
          const line = buffer.substring(0, newlineIndex)
          buffer = buffer.substring(newlineIndex + 1)
          if (!line || typeof line !== 'string') continue
          
          const trimmedLine = line.trim()
          
          // 跳过空行
          if (!trimmedLine) continue
          
          // 处理 data: 开头的 SSE 格式数据
          if (trimmedLine.startsWith('data:')) {
            try {
              // 提取 JSON 部分（去掉 "data:" 前缀）
              if (trimmedLine.length < 5) continue
              const jsonStr = trimmedLine.slice(5).trim()
              if (!jsonStr) continue
              
              const data = JSON.parse(jsonStr)

              // 检查是否是 error 类型，如果是则直接终止
              if (data.type === 'error') {
                isDone = true
                const errorMessage = data.error || '模型服务错误'
                const error = new Error(errorMessage)
                if (onError) {
                  onError(error)
                }
                throw error
              }

              // 先判断 type 是否为 done，如果是 done 则直接返回，不处理 delta
              if (data.type === 'done' || data.done === true) {
                isDone = true
                // 如果后端返回了 full_text，优先使用它（更完整准确）
                const finalText = data.full_text || data.fullText || fullText
                if (onComplete) {
                  onComplete(finalText, data)
                }
                return finalText
              }
              
              // 如果不是 done 类型，才处理 delta（流式内容）
              if (data.type !== 'done' && data.delta !== undefined && data.delta !== null) {
                fullText += data.delta
                if (onChunk) {
                  onChunk(data.delta, fullText)
                }
              }
            } catch (e) {
              // JSON 解析错误
              console.warn('Failed to parse SSE data:', trimmedLine, e)
            }
          }
        }
      }
    }

    // 如果流结束但没有收到 done 信号，也调用 onComplete
    if (onComplete && !isDone) {
      onComplete(fullText, null)
    }

    return fullText
  } catch (error) {
    console.error('Stream chat error:', error)
    if (onError) {
      onError(error)
    }
    throw error
  }
}

/**
 * 取消流式请求
 * @param {ReadableStreamDefaultReader} reader - 流读取器
 */
export function cancelStream(reader) {
  if (reader) {
    reader.cancel()
  }
}
